import { Play, FileText, BookOpen, Info, Shield, ArrowRight, Library } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { track } from '@/lib/analytics/mixpanel';
import { cn } from '@/lib/utils';
import { useState } from 'react';

const ARCADE_LINK = 'https://app.arcade.software/share/DUcYsFzXvlfHcQ3kR0R9?ref=share-link';

interface NavProofMenuProps {
  className?: string;
  onItemClick?: () => void;
}

/* ---- Shared item renderer ---- */

interface MenuItemDef {
  icon: React.ElementType;
  label: string;
  helper: string;
  trackEvent: string;
  action: 'demo' | 'navigate' | 'scroll';
  route?: string;
  scrollId?: string;
  iconAccent?: boolean;
}

const GROUP_START: MenuItemDef[] = [
  {
    icon: Play,
    label: 'Try Interactive Demo',
    helper: 'See Verithos in action (2–3 min)',
    trackEvent: 'nav_resources_demo_click',
    action: 'demo',
    iconAccent: true,
  },
  {
    icon: FileText,
    label: 'View Sample Draft',
    helper: 'See a submission-ready research draft',
    trackEvent: 'nav_resources_sample_click',
    action: 'navigate',
    route: '/sample-draft',
  },
];

const GROUP_UNDERSTAND: MenuItemDef[] = [
  {
    icon: Info,
    label: 'What is Verithos?',
    helper: 'Definition, methodology, and boundaries',
    trackEvent: 'nav_resources_what_click',
    action: 'navigate',
    route: '/what-is-verithos',
  },
  {
    icon: FileText,
    label: 'Verithos vs LLMs',
    helper: 'Why workflow + traceability matters',
    trackEvent: 'nav_resources_vs_llms_click',
    action: 'navigate',
    route: '/verithos-vs-llms',
  },
  {
    icon: FileText,
    label: 'QDAS Comparison 2026',
    helper: 'vs NVivo, ATLAS.ti, MAXQDA, Dedoose',
    trackEvent: 'nav_resources_qdas_click',
    action: 'navigate',
    route: '/verithos-vs-nvivo-atlasti-maxqda-dedoose',
  },
  {
    icon: Library,
    label: 'Evidence Brief',
    helper: 'Whitepaper on method, governance, and limits',
    trackEvent: 'nav_resources_whitepaper_click',
    action: 'navigate',
    route: '/whitepaper',
  },
];

const GROUP_USE: MenuItemDef[] = [
  {
    icon: BookOpen,
    label: 'How to Cite',
    helper: 'Disclosure guidance for researchers',
    trackEvent: 'nav_resources_cite_click',
    action: 'navigate',
    route: '/how-to-cite',
  },
  {
    icon: Shield,
    label: 'Privacy & Security',
    helper: 'How Verithos handles data',
    trackEvent: 'nav_resources_privacy_click',
    action: 'navigate',
    route: '/privacy-policy',
  },
];

/* ================================================================== */
/*  Desktop popover                                                    */
/* ================================================================== */

export function NavProofMenu({ className, onItemClick }: NavProofMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [open, setOpen] = useState(false);
  // Track whether we're navigating so we can force-unmount the popover instantly
  const [navigating, setNavigating] = useState(false);

  const close = () => {
    setOpen(false);
    onItemClick?.();
  };

  const handleAction = (item: MenuItemDef) => {
    track(item.trackEvent);
    // Force-unmount the popover immediately to prevent frozen dropdown
    setNavigating(true);
    setOpen(false);
    onItemClick?.();

    // Use microtask to ensure React has flushed the unmount before navigating
    queueMicrotask(() => {
      if (item.action === 'demo') {
        if (isHomePage) {
          const el = document.getElementById('interactive-demo');
          if (el) { el.scrollIntoView({ behavior: 'smooth' }); setNavigating(false); return; }
        }
        window.open(ARCADE_LINK, '_blank', 'noopener');
        setNavigating(false);
      } else if (item.action === 'scroll' && item.scrollId) {
        if (isHomePage) {
          document.getElementById(item.scrollId)?.scrollIntoView({ behavior: 'smooth' });
        } else {
          navigate('/', { state: { scrollTo: item.scrollId } });
        }
        setNavigating(false);
      } else if (item.action === 'navigate' && item.route) {
        navigate(item.route);
        setNavigating(false);
      }
    });
  };

  const renderItem = (item: MenuItemDef) => {
    const Icon = item.icon;
    return (
      <button
        key={item.label}
        onClick={() => handleAction(item)}
        className="group flex items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-all duration-150 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 w-full"
      >
        <div className={cn(
          'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors',
          item.iconAccent
            ? 'bg-primary/10 text-primary group-hover:bg-primary/15'
            : 'bg-muted text-muted-foreground group-hover:bg-muted/80'
        )}>
          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <span className="text-sm font-medium text-foreground">{item.label}</span>
          <p className="mt-0.5 text-[11px] text-muted-foreground leading-relaxed">{item.helper}</p>
        </div>
      </button>
    );
  };

  const GroupLabel = ({ children }: { children: string }) => (
    <span className="block px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/50">
      {children}
    </span>
  );

  const Divider = () => <div className="mx-3 my-1 border-t border-border/30" />;

  return (
    <Popover open={open && !navigating} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'px-4 py-2 2xl:px-5 2xl:py-2.5 text-sm 2xl:text-base font-medium rounded-lg transition-all duration-200',
            'text-muted-foreground hover:text-foreground hover:bg-muted/50',
            className,
          )}
        >
          Resources
        </button>
      </PopoverTrigger>
      {!navigating && (
        <PopoverContent
          align="center"
          sideOffset={8}
          className="w-80 p-2 backdrop-blur-xl bg-popover/95 border-border/40 shadow-xl rounded-xl"
        >
          <nav aria-label="Resources" className="flex flex-col gap-0">
            <GroupLabel>Start here</GroupLabel>
            {GROUP_START.map(renderItem)}

            <Divider />
            <GroupLabel>Understand Verithos</GroupLabel>
            {GROUP_UNDERSTAND.map(renderItem)}

            <Divider />
            <GroupLabel>Use it properly</GroupLabel>
            {GROUP_USE.map(renderItem)}

            <Divider />
            <button
              onClick={() => {
                track('nav_resources_view_all_click');
                handleAction({ icon: ArrowRight, label: 'View all resources', helper: '', trackEvent: 'nav_resources_view_all_click', action: 'navigate', route: '/resources' });
              }}
              className="flex items-center justify-between px-3 py-2.5 text-sm font-medium text-primary hover:bg-primary/5 rounded-lg transition-colors"
            >
              <span>View all resources</span>
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </nav>
        </PopoverContent>
      )}
    </Popover>
  );
}

/* ================================================================== */
/*  Mobile inline items                                                */
/* ================================================================== */

export function NavProofMobileItems({ onItemClick }: { onItemClick: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const handleAction = (item: MenuItemDef) => {
    track(`${item.trackEvent}_mobile`);
    onItemClick();
    if (item.action === 'demo') {
      if (isHomePage) {
        const el = document.getElementById('interactive-demo');
        if (el) { el.scrollIntoView({ behavior: 'smooth' }); return; }
      }
      window.open(ARCADE_LINK, '_blank', 'noopener');
    } else if (item.action === 'scroll' && item.scrollId) {
      if (isHomePage) {
        document.getElementById(item.scrollId)?.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate('/', { state: { scrollTo: item.scrollId } });
      }
    } else if (item.action === 'navigate' && item.route) {
      navigate(item.route);
    }
  };

  const allItems = [...GROUP_START, ...GROUP_UNDERSTAND, ...GROUP_USE];

  return (
    <div className="space-y-0.5">
      <span className="block px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
        Resources
      </span>
      {allItems.map((item) => {
        const Icon = item.icon;
        return (
          <button
            key={item.label}
            onClick={() => handleAction(item)}
            className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 active:bg-muted"
          >
            <Icon className={cn('h-4 w-4', item.iconAccent && 'text-primary')} aria-hidden="true" />
            {item.label}
          </button>
        );
      })}
      <button
        onClick={() => {
          track('nav_resources_view_all_click_mobile');
          onItemClick();
          navigate('/resources');
        }}
        className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left text-sm font-medium text-primary hover:bg-primary/5 active:bg-primary/10"
      >
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
        View all resources
      </button>
    </div>
  );
}
